import { Button, Icon } from "@pepperstone/pepper-ui-kit";
import isEqual from "lodash/isEqual";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/general/drawer/Drawer.tsx";
import { getAssetUrl } from "@/utils/helpers.ts";
import styles from "./TransactionHistoryFilterDrawer.module.css";
import type {
  OptionType,
  SelectedValuesType,
} from "../transaction/transaction-history-table-filter/types";
import type { TransactionHistoryTableFilterProps } from "../transaction/transaction-history-table-filter/TransactionHistoryTableFilter";

type FilterCategory =
  | "time"
  | "transaction"
  | "paymentMethod"
  | "account"
  | "currency"
  | "status";

const TransactionHistoryFilterDrawer = ({
  isLoading,
  selectedValues,
  timeOptions,
  transactionOptions,
  paymentMethodOptions,
  accountOptions,
  currencyOptions,
  statusOptions,
  setSelectedValues,
  btnIsDisabled,
  handleReset,
  onClose,
  defaultValues,
}: TransactionHistoryTableFilterProps & {
  onClose: () => void;
  defaultValues: SelectedValuesType;
}) => {
  const { t } = useTranslation();
  const [currentCategory, setCurrentCategory] = useState<FilterCategory | null>(
    null
  );
  const [tempSelectedValues, setTempSelectedValues] =
    useState<SelectedValuesType>(selectedValues);

  const filterCategories = [
    {
      key: "time" as const,
      label: t("transactions.history.filter.time"),
      options: timeOptions,
    },
    {
      key: "transaction" as const,
      label: t("transactions.history.filter.transaction"),
      options: transactionOptions,
    },
    {
      key: "paymentMethod" as const,
      label: t("transactions.history.filter.paymentMethod"),
      options: paymentMethodOptions,
    },
    {
      key: "account" as const,
      label: t("transactions.history.filter.account"),
      options: accountOptions,
    },
    {
      key: "currency" as const,
      label: t("transactions.history.filter.currency"),
      options: currencyOptions,
    },
    {
      key: "status" as const,
      label: t("transactions.history.filter.status"),
      options: statusOptions,
    },
  ];

  const handleSelectOption = useCallback(
    (category: FilterCategory, option: OptionType) => {
      setTempSelectedValues({
        ...tempSelectedValues,
        [category]: option,
      });
    },
    [tempSelectedValues]
  );

  const handleApplyFilters = useCallback(() => {
    setSelectedValues(tempSelectedValues);
    onClose();
  }, [tempSelectedValues, setSelectedValues, onClose]);

  const handleResetFilters = useCallback(() => {
    handleReset();
    // Get the default selected values after reset is called
    setTempSelectedValues(defaultValues);
    setSelectedValues(defaultValues);
    onClose();
  }, [handleReset, onClose, defaultValues, setSelectedValues]);

  const handleCloseCategory = useCallback(() => {
    setCurrentCategory(null);
  }, []);

  const getFilterLabel = (category: FilterCategory) => {
    const option = tempSelectedValues[category];
    return option?.label || "";
  };

  const icon = getAssetUrl("check-circle-filled.svg", "icons");

  if (currentCategory) {
    const category = filterCategories.find((c) => c.key === currentCategory);
    if (!category) return null;

    return (
      <Drawer
        title={category.label}
        onClose={onClose}
        showBackButton
        onBack={handleCloseCategory}
      >
        <div className={styles.optionsList}>
          {category.options.map((option) => (
            <button
              type="button"
              key={option.value}
              className={styles.optionItem}
              onClick={() => {
                handleSelectOption(currentCategory, option);
                handleCloseCategory();
              }}
            >
              <div className={styles.optionContent}>
                {typeof option.label === "string" ? (
                  <span className={styles.optionLabel}>{option.label}</span>
                ) : (
                  option.label
                )}
              </div>
              {tempSelectedValues[currentCategory]?.value === option.value && (
                <img src={icon} alt={"selected"} />
              )}
            </button>
          ))}
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer title={t("transactions.history.filter.title")} onClose={onClose}>
      <div className={styles.filtersList}>
        {filterCategories.map((category) => (
          <button
            type="button"
            key={category.key}
            className={styles.filterItem}
            onClick={() => setCurrentCategory(category.key)}
            disabled={
              isLoading ||
              (category.key === "currency" && category.options.length === 0)
            }
          >
            <div className={styles.filterContent}>
              <span className={styles.filterLabel}>{category.label}</span>
            </div>
            <div className={styles.filterEnd}>
              <span className={styles.filterValue}>
                {getFilterLabel(category.key)}
              </span>
              <Icon icon="arrow-right" size={12} />
            </div>
          </button>
        ))}
      </div>

      <div className={styles.actionButtons}>
        <Button
          type="button"
          size="ps-btn-md"
          variant="ps-btn-primary"
          onClick={handleApplyFilters}
          disabled={isLoading || isEqual(tempSelectedValues, selectedValues)}
          className={styles.applyButton}
        >
          {t("transactions.history.filter.apply")}
        </Button>
        <Button
          type="button"
          size="ps-btn-md"
          variant="ps-btn-secondary"
          onClick={handleResetFilters}
          disabled={isLoading || btnIsDisabled}
          className={styles.resetButton}
        >
          {t("transactions.history.filter.reset")}
        </Button>
      </div>
    </Drawer>
  );
};

export default TransactionHistoryFilterDrawer;
